import { useEffect, useState, useRef } from "react";
import APIManager from "../../managers/APIManager";
import * as Icon from "react-feather";
import { useTranslation } from "react-i18next";
import { upperCase } from "../../utils/manipulateText";
import Button from "../common/Button";
import { AppActionCreators } from "../../store/actions/app";
import { useDispatch, useSelector } from "react-redux";
import AppQuickFilterModal from "./AppQuickFilterModal";
import { capitalize } from "lodash";
import Loading from "../common/Loading";
import MessageText from "../common/MessageText";
import { NavLink } from "react-router";
import { breakdowns, quickDateButtons } from "./constants";

const AppQuickFilters = ({ customStyle = "", handleClick, handleRedirect }) => {
  const selectedUser = useSelector((state) => state?.site?.selectedUser);
  const [showItems, setShowItems] = useState(false);
  const [items, setItems] = useState([]);
  // dateRange = enum('LH', 'Y', 'T', '7D', '30D', 'MTD', 'LM', 'L3M', 'L6M')
  const wrapperRef = useRef(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [refreshItems, setRefreshItems] = useState(false);
  const [loading, setLoading] = useState(false);

  const getDateRangeFromKey = (key) => {
    const dateRange = quickDateButtons.find((dr) => dr.range === key);
    return dateRange ? dateRange.text : "Y";
  };

  useEffect(() => {
    const fetchFilters = async () => {
      setLoading(true);
      try {
        const response = await APIManager.getAppFilters();
        setItems(response?.data || []);
      } catch (error) {
        console.error("Error fetching app filters:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFilters();
  }, [refreshItems]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowItems(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDelete = async (filterId) => {
    try {
      await APIManager.deleteAppFilter(filterId);
      setRefreshItems((prev) => !prev);
    } catch (error) {
      console.error("Error deleting filter:", error);
    }
  };

  const updateAndOpenModal = (item) => (dispatch) => {
    dispatch(
      AppActionCreators.setSelectedAppQuickFilter({
        mode: 1,
        countries: item?.countries || [],
        apps: item?.apps || [],
        appUser: item?.appUser,
        name: item?.name || "",
        dateRange: item?.dateRange || "",
        breakdowns: item?.breakdowns || ["daily"],
        platform: item?.platform || "all",
        id: item?.id,
      })
    );

    dispatch(AppActionCreators.setAppQuickFilterModal(true));
  };

  return (
    <>
      <AppQuickFilterModal setRefreshItems={setRefreshItems} />
      <div ref={wrapperRef} className={`relative min-w-64 z-70 ${customStyle}`}>
        <div
          onClick={() => setShowItems(!showItems)}
          className="flex flex-row items-center cursor-pointer p-1 rounded-md px-2 hover:bg-gray-100"
        >
          <Icon.Filter className="h-5 w-5 text-gray-600 my-auto mx-1" />
          <span className="bg-transparent rounded-md p-1 font-semibold text-xs">
            {upperCase(t("quick filters"))}
          </span>
          <Icon.ChevronDown
            className={`transform ${
              showItems ? "rotate-180" : ""
            } transition-transform`}
            width="10"
            height="10"
          />
        </div>
        {showItems && (
          <div
            className="absolute right-0 mt-1 bg-gray-100 shadow-lg rounded-lg z-10 w-auto"
            style={{ minWidth: "150%" }}
          >
            <div
              className="p-2 border border-gray-300 rounded-md divide-y overflow-auto"
              style={{ maxHeight: "400px" }}
            >
              {loading && <Loading />}
              {items.length < 1 && (
                <MessageText
                  message={t(
                    "there are no filters available for your account."
                  )}
                  textAlignment="text-center"
                />
              )}
              {items.map((item, index) => (
                <div
                  key={index}
                  className="px-2 py-2 hover:bg-gray-200 rounded-md"
                >
                  <div className="flex flex-row justify-between whitespace-no-wrap divide-x mx-1">
                    <NavLink
                      className="mx-1 px-1 w-full cursor-pointer hover:text-indigo-500 mr-2"
                      onClick={() => {
                        handleClick(item);
                        setShowItems(false);
                      }}
                      to={() => handleRedirect(item.appId || "all-apps")}
                    >
                      <div className="font-semibold text-base">
                        {item.name || `<Filter ${index + 1}>`}
                        <span className="font-normal text-xs">{` \u2022 ${
                          item.appUser?.name || "All Apps"
                        }`}</span>
                      </div>
                      <span className="font-normal text-xs">
                        {`${item.breakdowns
                          .map((i) => capitalize(t(breakdowns[i])))
                          .join(", ")} \u2022 ${capitalize(
                          t(item.platform)
                        )} \u2022 ${capitalize(
                          t(getDateRangeFromKey(item.dateRange))
                        )}`}
                      </span>
                    </NavLink>
                    <div className="flex flex-row">
                      <div
                        className="px-1 cursor-pointer flex justify-center items-center"
                        onClick={() => {
                          setShowItems(false);
                          dispatch(updateAndOpenModal(item));
                        }}
                      >
                        <Icon.Settings className="h-5 w-5 text-gray-600 my-auto mx-1 hover:text-indigo-500" />
                      </div>
                      <div
                        className="px-1 cursor-pointer flex justify-center items-center"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Icon.Trash2 className="h-5 w-5 text-gray-600 my-auto mx-1 hover:text-red-500" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-row justify-center m-2 items-stretch">
              <Button
                color="primary"
                className="m-1 flex-auto"
                onClick={() => {
                  setShowItems(false);
                  dispatch(
                    AppActionCreators.setSelectedAppQuickFilter({
                      mode: 0,
                      dateRange: "Y",
                      breakdowns: ["appNames"],
                      platform: "all",
                      appUser: {
                        id: selectedUser?.id,
                        name: selectedUser?.name,
                      },
                      apps: [],
                      countries: [],
                    })
                  );
                  dispatch(AppActionCreators.setAppQuickFilterModal(true));
                }}
              >
                {upperCase(t("create new filter"))}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AppQuickFilters;
